import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { CustomerService } from '../../services/customer.service';
import { Customer } from '../../interfaces/sales/customer.interface';
import { SalesInvoiceService } from '../../services/salesInvoice.service';
import { SalesInvoice, SALES_INVOICE_STATUS_COLORS } from '../../interfaces/sales/salesInvoice.interface';
import { useToast } from '../../context/ToastContext';

const CustomerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [invoices, setInvoices] = useState<SalesInvoice[]>([]);

  useEffect(() => {
    if (!id) return;
    CustomerService.getById(id)
      .then(c => setCustomer(c))
      .catch(e => toast.showApiError(e, 'Failed to load customer'))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!id) return;
    SalesInvoiceService.listByCustomer(id)
      .then(list => setInvoices(list))
      .catch(e => toast.showApiError(e, 'Failed to load invoices'));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async () => {
    if (!customer) return;
    if (!window.confirm(`Delete customer "${customer.name}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await CustomerService.remove(customer.id);
      toast.showSuccess('Customer deleted');
      navigate('/sales/customers');
    } catch (e) {
      // Surface backend 400 (open documents) and other errors via toast — do NOT swallow.
      toast.showApiError(e, 'Failed to delete customer');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  if (!customer) return <Box sx={{ p: 3 }}><Typography>Customer not found.</Typography></Box>;

  const outstanding = invoices
    .filter(inv => inv.status === 'ISSUED' || inv.status === 'PARTIALLY_PAID')
    .reduce((sum, inv) => sum + (inv.totalAmount - inv.paidAmount), 0);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">{customer.name}</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => navigate(`/sales/customers/${customer.id}/edit`)}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
          <Button variant="outlined" onClick={() => navigate(-1)}>Back</Button>
        </Box>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 1, alignItems: 'center' }}>
          <Typography color="text.secondary">Name</Typography>
          <Typography>{customer.name}</Typography>
          <Typography color="text.secondary">Contact Person</Typography>
          <Typography>{customer.contactPerson ?? '—'}</Typography>
          <Typography color="text.secondary">Email</Typography>
          <Typography>{customer.email ?? '—'}</Typography>
          <Typography color="text.secondary">Phone</Typography>
          <Typography>{customer.phone ?? '—'}</Typography>
          <Typography color="text.secondary">Address</Typography>
          <Typography>{customer.address ?? '—'}</Typography>
          <Typography color="text.secondary">Tax Reg No</Typography>
          <Typography>{customer.taxRegistrationNo ?? '—'}</Typography>
        </Box>
      </Paper>


      {/* Outstanding balance */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>Outstanding Balance</Typography>
        <Typography variant="h4" color={outstanding > 0 ? 'error' : 'text.primary'}>
          {outstanding.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </Typography>
      </Paper>

      {/* Recent invoices */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Recent Invoices</Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate(`/sales/reports/statement?customerId=${customer.id}`)}
          >
            View Statement
          </Button>
        </Box>
        {invoices.length === 0 ? (
          <Typography color="text.secondary">No invoices found.</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Invoice #</TableCell>
                  <TableCell>Issue Date</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices
                  .slice()
                  .sort((a, b) => (b.issueDate ?? '').localeCompare(a.issueDate ?? ''))
                  .slice(0, 5)
                  .map(inv => (
                    <TableRow
                      key={inv.id}
                      hover
                      component={Link}
                      to={`/sales/invoices/${inv.id}`}
                      sx={{ textDecoration: 'none', cursor: 'pointer' }}
                    >
                      <TableCell>{inv.invoiceNumber ?? 'Draft'}</TableCell>
                      <TableCell>{inv.issueDate}</TableCell>
                      <TableCell align="right">
                        {inv.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={inv.status}
                          color={SALES_INVOICE_STATUS_COLORS[inv.status]}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        {(inv.totalAmount - inv.paidAmount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default CustomerDetail;
